// @flow

import {copyToClipboard} from 'docs/components/contrib/CopyButton/CopyToClipboard';
import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import Box from 'sail/Box';
import {ButtonLink} from 'sail/Button';
import Group from 'sail/Group';
import HoverHandler from 'sail/handler/HoverHandler';
import Icon from 'sail/Icon';
import {Body} from 'sail/Text';
import {TooltipContainer} from 'sail/Tooltip';

const messages = defineMessages({
  clickToCopy: {
    id: 'docsApp.components.contrib.roleDescription.clickToCopy',
    description:
      'Text that appears when you hover over the copyable text, explaining you can click to copy it.',
    defaultMessage: 'Click to copy',
  },
  copied: {
    id: 'docsApp.components.contrib.roleDescription.copied',
    description: 'Text that appears after you copy a role id.',
    defaultMessage: 'Copied',
  },
});

export function useCopyTextHandler({
  text,
  timeout,
}: {text: string, timeout: number} = {}): [boolean, () => void] {
  const [copied, setCopied] = React.useState(false);

  const onCopy = React.useCallback(
    function handleClick() {
      copyToClipboard(text);
      setCopied(true);
    },
    [text],
  );

  React.useEffect(() => {
    if (copied && timeout) {
      const timeoutId = setTimeout(setCopied, timeout, false);

      return () => clearTimeout(timeoutId);
    }
  }, [copied, timeout]);

  return [copied, onCopy];
}

function CopyTextButton({copied, onClick, icon}) {
  return (
    <HoverHandler>
      {(hoverProps, isHovering) => (
        <Box
          style={{
            borderRadius: 4,
            transition: 'all 200ms',
            display: 'inline-block',
          }}
          padding={{horizontal: 4}}
          /* eslint-disable no-nested-ternary */
          background={copied ? 'blue100' : isHovering ? 'gray50' : undefined}
          {...hoverProps}
        >
          {/* $FlowFixMe122 - go/flowfixme122 */}
          <ButtonLink
            icon={icon}
            iconPosition="right"
            color="gray"
            onClick={onClick}
          />
        </Box>
      )}
    </HoverHandler>
  );
}

function adjustOffsets(contextMP, layerMP) {
  const offset = 8;
  let offsetY = -offset;
  let offsetX = 0;
  if (layerMP === 'top') {
    offsetY = offset;
  } else if (layerMP === 'right') {
    offsetY = 0;
    offsetX = -offset;
  } else if (layerMP === 'left') {
    offsetY = 0;
    offsetX = offset;
  }
  return {x: offsetX, y: offsetY};
}

type CopyButtonProps = {|
  textToCopy: string,
  timeout?: number,
  CustomCopyButton?: (props: any) => React.Node,
|};

export default function CopyButton({
  textToCopy,
  CustomCopyButton,
  timeout = 1000,
  ...rest
}: CopyButtonProps) {
  const mouseOver = React.useRef(false);
  const tooltip = React.useRef(null);

  const [copied, onClick] = useCopyTextHandler({
    text: textToCopy,
    timeout,
  });

  const [wasCopied, setWasCopied] = React.useState(false);
  React.useEffect(() => {
    if (copied) {
      setWasCopied(true);
    }
  }, [copied]);

  React.useEffect(() => {
    if (tooltip.current && wasCopied && !copied) {
      tooltip.current.handleHide();
    }
  }, [copied, wasCopied]);

  return (
    <>
      <TooltipContainer
        ref={tooltip}
        arrowStyle={{display: 'none'}}
        adjustOffsets={adjustOffsets}
        autoflip
        contentPadding={0}
        showDelay={0}
        tooltipContents={
          <Box background="gray700" padding={{vertical: 4, horizontal: 8}}>
            <Body color="white" display="block">
              {wasCopied ? (
                <Group spacing={4} direction="horizontal" alignItems="center">
                  <Icon icon="check" size={12} color="white" />
                  <FormattedMessage {...messages.copied} />
                </Group>
              ) : (
                <>
                  <FormattedMessage {...messages.clickToCopy} />
                </>
              )}
            </Body>
          </Box>
        }
        context={
          CustomCopyButton ? (
            <CustomCopyButton
              {...rest}
              textToCopy={textToCopy}
              copied={copied}
              onClick={onClick}
            />
          ) : (
            <CopyTextButton
              {...rest}
              copied={copied}
              onClick={onClick}
              icon="clipboard"
            />
          )
        }
        variableWidth
        onHide={() => {
          mouseOver.current = false;
        }}
        onShow={() => {
          mouseOver.current = true;
          setWasCopied(false);
        }}
      />
    </>
  );
}
